import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { NavController } from '@ionic/angular';
import { TokenService } from '@tablesready/frontend-auth';
import { AuthRestaurantService, AuthUserService } from '@tablesready/frontend-store';

import {
  catchError,
  combineLatest,
  exhaustMap,
  filter,
  from,
  map,
  Observable,
  of,
  pairwise,
  switchMap,
  tap,
} from 'rxjs';
import { PermissionService } from '@tablesready/frontend-permissions';

// Pages that show one location's data, which the API refuses to a session without a location
const LOCATION_PAGES = [
  '/waitlist',
  '/reservations',
  '/floorplan',
  '/history',
  '/analytics',
  '/customers',
  '/togo',
  '/import',
];
const PROFILE_PAGE = '/profile/details';

function isLocationPage(url: string): boolean {
  const path = url.split(/[?#]/u)[0];

  return LOCATION_PAGES.some(page => path === page || path.startsWith(`${page}/`));
}

@Injectable()
export class PermissionCheckerService {
  private readonly navController = inject(NavController);
  private readonly tokenService = inject(TokenService);

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly authRestaurantService: AuthRestaurantService,
    private readonly permissionService: PermissionService,
    private readonly userService: AuthUserService,
  ) {
  }

  listenChanges() {
    return combineLatest([
      this.authRestaurantService.select()
        .pipe(
          pairwise(),
          filter(([prev, current]) => prev === null
            || prev.location?._id !== current.location?._id
            || prev.locationUser?.scope !== current.locationUser?.scope
            || prev.accountUser?.role !== current.accountUser?.role
          ),
          map(([, current]) => current),
        ),
      this.router.events.pipe(
        filter(event => event instanceof NavigationEnd),
        switchMap(() => {
          let route = this.route;
          while (route.firstChild) {
            route = route.firstChild;
          }

          return route.data;
        })
      ),
    ])
      .pipe(
        // redirectWithoutLocation moves a session that has no location
        filter(() => !this.tokenService.hasNoLocationToken()),
        map(([user, {scopes, availableFor}]) => {
          if (scopes && availableFor) {
            return this.permissionService.userAccountHasScopes(user, scopes, availableFor);
          }

          return true;
        }),
        tap(result => {
          if (!result) {
            const redirectUrl = this.router.url === '/waitlist'
              ? PROFILE_PAGE
              : '/waitlist';
            this.router.navigate([redirectUrl]);
          }
        }),
      );
  }

  /**
   * Moves a session with no location (removed from its last one, or never given one) off the
   * location pages, one navigation at a time. Root navigation also closes the pages Ionic keeps
   * cached behind it.
   */
  redirectWithoutLocation(): Observable<boolean> {
    return combineLatest([
      this.authRestaurantService.select(),
      this.router.events.pipe(
        filter((routerEvent): routerEvent is NavigationEnd => routerEvent instanceof NavigationEnd),
        map(navigationEnd => navigationEnd.urlAfterRedirects),
      ),
    ])
      .pipe(
        filter(([, url]) => this.tokenService.hasNoLocationToken() && isLocationPage(url)),
        // A failed navigation must not end the redirects for the rest of the session
        exhaustMap(() => from(this.navController.navigateRoot(PROFILE_PAGE))
          .pipe(catchError(() => of(false)))),
      );
  }

  // Redirects user to profile if email or name is empty
  isProfileFilled() {
    return combineLatest([
      this.userService.select().pipe(map(user => !!(user.email && user.name))),
      this.router.events.pipe(
        filter(event => event instanceof NavigationEnd),
        map(() => this.router.url),
      ),
    ])
      .pipe(
        tap(([isProfileFilled, navigatedUrl]) => {
          if (!isProfileFilled && navigatedUrl !== PROFILE_PAGE) {
             void this.router.navigateByUrl(PROFILE_PAGE);
          }
        }),
      );
  }
}
